import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import Joyride from "react-joyride";
import JoyRideTooltip from "./JoyrideToolTip";
import { useTheme } from "styled-components/native";
import { Platform, DeviceEventEmitter } from "react-native";
import { useSelector } from "react-redux";
import { getFilteredSteps } from "./utils";
import { RootState } from "@redux/store";
import { selectViewTypeForCategory } from "@redux/slices/userInfoSlice";

const JoyrideContext = createContext(null);

export const useJoyride = () => useContext(JoyrideContext);

export const JoyrideProvider = ({
  children,
  steps,
  disableScrolling,
}: {
  children: React.ReactNode;
  steps: any;
  disableScrolling: boolean;
}) => {
  const theme = useTheme();
  const [joyrideQueue, setJoyrideQueue] = useState<
    { screen: string; order: number }[]
  >([]);
  const [currentScreen, setCurrentScreen] = useState(null);
  const activeProduct = useSelector(
    (state: any) => state.appState?.activeProduct
  );
  const selectedViewType = useSelector((state: RootState) =>
    selectViewTypeForCategory(state, activeProduct)
  );
  const filteredSteps = getFilteredSteps(
    steps,
    currentScreen ?? "",
    activeProduct,
    selectedViewType
  );
  const [stepIndex, setStepIndex] = useState(0);
  const ref = useRef(null);
  const startTourForScope = (screen: string) => {
    setCurrentScreen(screen as any);
    setJoyrideQueue((prevQueue) => {
      const newQueue = [...prevQueue];
      const screenData = filteredSteps[screen];

      if (
        screenData &&
        !newQueue?.some((item: any) => item?.screen === screen)
      ) {
        newQueue?.push({ screen, order: screenData?.order });
        newQueue?.sort((a: any, b: any) => a?.order - b?.order);
      }

      return newQueue;
    });
  };

  const resetScopeForScope = (screen: string) => {
    setJoyrideQueue((prevQueue) =>
      prevQueue?.filter((item) => item?.screen !== screen)
    );
    if (currentScreen === screen) {
      setCurrentScreen(null);
    }
  };

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(
      "resetJoyrideScope",
      (screen) => {
        try {
          if (screen === "all") {
            setJoyrideQueue([]);
            setCurrentScreen(null);
          } else {
            resetScopeForScope(screen);
          }
        } catch (e) {
          // ignore
        }
      }
    );
    return () => sub.remove();
  }, [currentScreen]);

  const getScopedSteps =
    filteredSteps[currentScreen ?? ""]?.steps?.map((a: any) => ({
      ...a,
      target: `#${a.target}`,
    })) || [];

  useEffect(() => {
    setTimeout(() => {
      (ref.current as any)?.helpers?.open();
    });
  }, [currentScreen]);

  useEffect(() => {
    if (!currentScreen && joyrideQueue?.length > 0) {
      setCurrentScreen(joyrideQueue?.[0]?.screen as any);
    }
  }, [joyrideQueue, currentScreen]);

  const handleTourEnd = () => {
    if (currentScreen) {
      setStepIndex(0);
      resetScopeForScope(currentScreen);
      setCurrentScreen(null);
    }
  };

  const contextValue = {
    startTourForScope,
    resetScopeForScope,
    setStepIndex,
  };

  if (!theme.isLargeScreen && Platform.OS !== "web") {
    return <>{children}</>;
  }

  return (
    <JoyrideContext.Provider value={contextValue as any}>
      {children}
      <>
        <Joyride
          ref={ref}
          key={currentScreen}
          steps={getScopedSteps}
          stepIndex={stepIndex}
          tooltipComponent={(props) => (
            <JoyRideTooltip
              setStepIndex={setStepIndex}
              {...props}
              handleTourEnd={handleTourEnd}
            />
          )}
          run={!!currentScreen}
          continuous={true}
          floaterProps={{ autoOpen: true }}
          styles={{ options: { arrowColor: theme["core-01"] } }}
          disableScrolling={disableScrolling}
          disableOverlayClose
          disableCloseOnEsc
        />
      </>
    </JoyrideContext.Provider>
  );
};
